import React, { useState } from 'react';
import { analyzeMoment } from '../services/geminiService';
import { MomentAnalysis } from '../types';
import { Sunset, RefreshCw, Feather } from 'lucide-react';
import { useUser } from '../context/UserContext';

const IchigoMoments: React.FC = () => {
  const [memory, setMemory] = useState('');
  const [analysis, setAnalysis] = useState<MomentAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp, unlockBadge } = useUser();

  const handleAnalyze = async () => {
    if (!memory.trim()) return;
    setLoading(true);
    setAnalysis(null);
    try {
      const data = await analyzeMoment(memory);
      setAnalysis(data);
      addXp(50, 'Crystalized a Golden Moment');
      unlockBadge('time_traveler');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-orange-50 text-orange-600 rounded-full mb-4">
          <Sunset size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">The Golden Hour</h1>
        <p className="text-nunchi-600">
          Relive a memory and discover why it can never happen again in exactly the same way. 
          Honor its impermanence.
        </p>
      </header>

      {!analysis && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                Describe a memory you want to keep
            </label>
            <textarea
              value={memory}
              onChange={(e) => setMemory(e.target.value)}
              placeholder="e.g., The time we watched fireworks on the beach in 2018..."
              className="w-full h-40 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-orange-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handleAnalyze}
              disabled={!memory.trim()}
              className="w-full bg-orange-700 text-white py-4 rounded-xl font-bold hover:bg-orange-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Feather size={18} /> Immortalize This Moment
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="w-16 h-16 border-4 border-orange-100 border-t-orange-600 rounded-full animate-spin mx-auto mb-4"></div>
             <p className="font-serif italic text-orange-700">Sifting through the sands of time...</p>
        </div>
      )}

      {analysis && (
        <div className="animate-fadeIn space-y-6">
            <div className="bg-orange-900 text-orange-50 p-8 rounded-2xl shadow-xl relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
                 
                 <h2 className="text-3xl font-serif font-bold mb-2 text-center">{analysis.title}</h2>
                 <div className="w-20 h-1 bg-orange-500 mx-auto mb-8 opacity-50"></div>

                 <div className="mb-6">
                    <h3 className="text-orange-300 uppercase tracking-widest text-xs font-bold mb-2">Unique Significance</h3>
                    <p className="leading-relaxed font-serif text-lg">{analysis.significance}</p>
                 </div>

                 <div className="mb-6 bg-white/10 p-4 rounded-xl">
                    <h3 className="text-orange-300 uppercase tracking-widest text-xs font-bold mb-2">Sensory Details</h3>
                    <p className="italic text-orange-100">{analysis.sensoryDetails}</p>
                 </div>

                 <div className="text-right">
                    <h3 className="text-orange-300 uppercase tracking-widest text-xs font-bold mb-1">On Impermanence</h3>
                    <p className="font-medium text-orange-200">"{analysis.impermanence}"</p>
                 </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={() => { setAnalysis(null); setMemory(''); }}
                  className="text-orange-600 hover:text-orange-800 font-bold flex items-center gap-2 mx-auto"
                >
                    <RefreshCw size={16} /> Honor Another Memory
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default IchigoMoments;